import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eyebrow, Reveal } from "@/components/Primitives";
import { BUSINESS_TYPES, SERVICES, errText, http } from "@/lib/site";

const EMPTY = {
  business_name: "", contact_name: "", email: "", phone: "", business_type: BUSINESS_TYPES[0],
  monthly_volume: "", current_rate: "", services: [], statement_review: true, message: "",
};

const L = ({ children }) => <label className="text-sm font-medium text-slate-700">{children}</label>;
const inputCls = "mt-2 h-12 rounded-xl bg-white border-slate-300 focus-visible:ring-[var(--cobalt)]";

const Success = ({ name, onReset }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-16 text-center" data-testid="lead-success">
    <CheckCircle2 className="mx-auto h-14 w-14 text-[var(--mint)]" />
    <h3 className="mt-6 font-display text-3xl font-semibold">Thanks{name ? `, ${name}` : ""}. You're in.</h3>
    <p className="mt-3 text-slate-600 max-w-md mx-auto">
      We've emailed you a confirmation. A specialist will reach out within 48 hours.
    </p>
    <button onClick={onReset} data-testid="lead-success-reset" className="mt-8 text-sm font-semibold underline underline-offset-4">
      Submit another request
    </button>
  </motion.div>
);

export const LeadForm = ({ prefill }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  useEffect(() => {
    if (!prefill) return;
    setF((s) => ({
      ...s,
      ...(prefill.monthly_volume ? { monthly_volume: String(prefill.monthly_volume) } : {}),
      ...(prefill.current_rate ? { current_rate: String(prefill.current_rate) } : {}),
      ...(prefill.business_type ? { business_type: prefill.business_type } : {}),
      ...(prefill.statement_review ? { statement_review: true } : {}),
      ...(prefill.service && !s.services.includes(prefill.service) ? { services: [...s.services, prefill.service] } : {}),
    }));
  }, [prefill]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const toggle = (svc) =>
    setF((s) => ({ ...s, services: s.services.includes(svc) ? s.services.filter((x) => x !== svc) : [...s.services, svc] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/leads", {
        ...f,
        monthly_volume: Number(f.monthly_volume) || 0,
        current_rate: f.current_rate === "" ? null : Number(f.current_rate),
      });
      setDone(f.contact_name.split(" ")[0]);
      setF(EMPTY);
      toast.success("Request received — expect a response within 48 hours.");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="quote" className="py-24 lg:py-36 bg-white" data-testid="lead-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <Eyebrow>05 — Free quote</Eyebrow>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.02]">
            Let's lower your <span className="font-serif-i text-[var(--cobalt)]">cost</span> of getting paid.
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed">
            Tell us a little about your business. You'll get an instant confirmation by email and a personal response
            from a specialist within 48 hours.
          </p>
          <div className="mt-10 space-y-4 text-sm text-slate-700">
            {["No contracts to sign to get a quote", "Retail, online, mobile — and payroll", "Your info is never sold or shared"].map((t) => (
              <p key={t} className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[var(--mint)]" />{t}</p>
            ))}
          </div>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="rounded-[32px] bg-[var(--paper)] border border-slate-200 p-6 sm:p-10">
            {done !== "" ? (
              <Success name={done} onReset={() => setDone("")} />
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="lead-form">
                <div><L>Business name *</L><Input required className={inputCls} value={f.business_name} onChange={set("business_name")} data-testid="lead-business-name" /></div>
                <div><L>Your name *</L><Input required className={inputCls} value={f.contact_name} onChange={set("contact_name")} data-testid="lead-contact-name" /></div>
                <div><L>Email *</L><Input required type="email" className={inputCls} value={f.email} onChange={set("email")} data-testid="lead-email" /></div>
                <div><L>Phone *</L><Input required type="tel" minLength={7} className={inputCls} value={f.phone} onChange={set("phone")} data-testid="lead-phone" /></div>
                <div>
                  <L>Business type</L>
                  <Select value={f.business_type} onValueChange={set("business_type")}>
                    <SelectTrigger className={inputCls} data-testid="lead-business-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`lead-type-option-${t.split(" ")[0].toLowerCase()}`}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><L>Monthly volume ($)</L><Input type="number" min="0" className={inputCls} value={f.monthly_volume} onChange={set("monthly_volume")} data-testid="lead-monthly-volume" /></div>
                  <div><L>Current rate (%)</L><Input type="number" step="0.01" min="0" max="15" className={inputCls} value={f.current_rate} onChange={set("current_rate")} data-testid="lead-current-rate" /></div>
                </div>
                <div className="sm:col-span-2">
                  <L>I'm interested in</L>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {SERVICES.map((s) => (
                      <button
                        type="button" key={s} onClick={() => toggle(s)}
                        data-testid={`lead-service-${s.split(" ")[0].toLowerCase()}`}
                        className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                          f.services.includes(s) ? "bg-[var(--cobalt)] text-white border-[var(--cobalt)]" : "bg-white border-slate-300 hover:border-[var(--ink)]"
                        }`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
                <label className="sm:col-span-2 flex items-start gap-3 rounded-xl bg-white border border-slate-200 p-4 cursor-pointer">
                  <Checkbox checked={f.statement_review} onCheckedChange={(v) => setF((s) => ({ ...s, statement_review: !!v }))} data-testid="lead-statement-review" className="mt-0.5" />
                  <span className="text-sm text-slate-700"><strong>Yes, I'd like a free statement review.</strong> We'll explain how to share your latest processing statement when we reach out.</span>
                </label>
                <div className="sm:col-span-2"><L>Anything else?</L><Textarea className="mt-2 rounded-xl bg-white border-slate-300 min-h-[96px]" value={f.message} onChange={set("message")} data-testid="lead-message" /></div>
                <button
                  type="submit" disabled={busy} data-testid="lead-submit"
                  className="sm:col-span-2 group inline-flex items-center justify-center gap-3 rounded-full bg-[var(--ink)] text-white px-7 py-4 font-semibold hover:bg-[var(--cobalt)] disabled:opacity-60 transition-colors"
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                  {busy ? "Sending…" : "Get my free quote"}
                  {!busy && <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
